import type { Exercise, ExerciseEquipment } from '../../types/exercise'
import type {
  ExerciseTemplate,
  ExerciseTemplateInput,
  ExerciseTemplatePayload,
} from '../../types/exercise-template'
import type { CustomExercisePayload } from '../../services/exercises'

/**
 * The form state and the rules behind the custom-exercise editor.
 *
 * A block (ExerciseTemplate) is `sets` × `reps_per_set` work bouts. Each bout is
 * either timed (`work_seconds` counts down on its own) or manual (`reps_target`
 * reps, the athlete taps to move on) — the server derives the stored `manual`
 * flag from whichever of the two it receives, and refuses a payload with both or
 * neither. `rest_seconds` sits between bouts inside a set, `set_rest_seconds`
 * between sets, and an `alternate_side` block runs every bout twice with an
 * optional `side_change_seconds` in between.
 */

export interface CustomExerciseFormData {
  title: string
  description: string
  categoryId: string
  durationMinutes: string
  equipment: ExerciseEquipment | ''
  templates: ExerciseTemplateInput[]
}

let keySeed = 0

function nextKey(): string {
  keySeed += 1
  return `tpl-${keySeed}`
}

export function emptyTemplateInput(id = ''): ExerciseTemplateInput {
  return {
    key: nextKey(),
    id,
    title: '',
    short_description: '',
    kind: 'normal',
    mode: 'timed',
    sets: 3,
    reps_per_set: 1,
    work_seconds: 10,
    reps_target: null,
    rest_seconds: 0,
    set_rest_seconds: 120,
    // Sliders have no "unset" position of their own, so these open on a usable
    // starting point; dragging either to zero is what clears it again.
    weight_percent: 50,
    rpe: 5,
    alternate_side: false,
    side_change_seconds: null,
    // What the timer already did between every pair of blocks.
    transition_kind: 'manual',
    transition_seconds: null,
  }
}

export function emptyFormData(title = ''): CustomExerciseFormData {
  return {
    title,
    description: '',
    categoryId: '',
    durationMinutes: '',
    equipment: '',
    templates: [],
  }
}

export function templateToInput(template: ExerciseTemplate): ExerciseTemplateInput {
  return {
    key: nextKey(),
    id: template.id,
    title: template.title ?? '',
    short_description: template.short_description ?? '',
    kind: template.kind,
    mode: template.manual ? 'manual' : 'timed',
    sets: template.sets,
    reps_per_set: template.reps_per_set,
    work_seconds: template.work_seconds,
    reps_target: template.reps_target,
    rest_seconds: template.rest_seconds,
    set_rest_seconds: template.set_rest_seconds,
    weight_percent: template.weight_percent,
    rpe: template.rpe,
    alternate_side: template.alternate_side,
    side_change_seconds: template.side_change_seconds,
    transition_kind: template.transition_kind ?? 'manual',
    transition_seconds: template.transition_seconds,
  }
}

/**
 * Fills the editor from an existing exercise. Used both to edit one of your own
 * and to duplicate any exercise you can see — a catalog protocol is the fastest
 * starting point for your own variant of it.
 */
export function exerciseToFormData(exercise: Exercise): CustomExerciseFormData {
  return {
    title: exercise.title,
    description: exercise.description ?? '',
    categoryId: exercise.category.id,
    durationMinutes: exercise.duration_minutes != null ? String(exercise.duration_minutes) : '',
    equipment: exercise.equipment ?? '',
    templates: (exercise.templates ?? []).map(templateToInput),
  }
}

/** Only ever sends one of work_seconds / reps_target — see the module comment. */
export function templateToPayload(input: ExerciseTemplateInput): ExerciseTemplatePayload {
  const payload: ExerciseTemplatePayload = {
    title: input.title.trim(),
    short_description: input.short_description.trim() || null,
    kind: input.kind,
    sets: input.sets ?? 1,
    reps_per_set: input.reps_per_set ?? 1,
    rest_seconds: input.rest_seconds ?? 0,
    set_rest_seconds: input.set_rest_seconds ?? 0,
    weight_percent: input.weight_percent,
    rpe: input.rpe,
    alternate_side: input.alternate_side,
    // The server rejects a side-change delay on a block that does not alternate.
    side_change_seconds: input.alternate_side ? input.side_change_seconds : null,
    transition_kind: input.transition_kind,
  }

  // Same rule for the pause: the duration exists only for the timed kind.
  if (input.transition_kind === 'timed') {
    payload.transition_seconds = input.transition_seconds ?? 0
  }

  if (input.mode === 'manual') {
    payload.reps_target = input.reps_target ?? 0
  } else {
    payload.work_seconds = input.work_seconds ?? 0
  }

  return payload
}

/**
 * The exercise itself, without its blocks.
 *
 * Blocks are saved one at a time against their own endpoint, and `templates` on
 * this payload means "replace the whole set" — sending it here would destroy
 * and recreate every block, losing the ids the editor is holding. So the two
 * are never sent together: this is what an edit of the details saves.
 */
export function metadataPayload(form: CustomExerciseFormData): CustomExercisePayload {
  const duration = Number.parseInt(form.durationMinutes, 10)

  return {
    title: form.title.trim(),
    description: form.description.trim() || null,
    exercise_category_id: form.categoryId,
    duration_minutes: Number.isFinite(duration) && duration > 0 ? duration : null,
    equipment: form.equipment || null,
  }
}

/**
 * Details plus every block, for the one moment they are written together:
 * creating an exercise, where duplicating brings the source's blocks along.
 */
export function formToPayload(form: CustomExerciseFormData): CustomExercisePayload {
  return {
    ...metadataPayload(form),
    templates: form.templates.map(templateToPayload),
  }
}

/**
 * Whether a block differs from what was last saved. Compares the payloads
 * rather than the inputs, so the local key and the fields the server does not
 * store (the work mode, which it derives) cannot make a block look dirty.
 */
export function isTemplateDirty(input: ExerciseTemplateInput, saved: ExerciseTemplateInput): boolean {
  return JSON.stringify(templateToPayload(input)) !== JSON.stringify(templateToPayload(saved))
}

/**
 * Field-keyed validation errors, mirroring the server's so the user is told
 * before a round trip. The values are i18n keys under
 * `trainingDiary.customExercise.errors`.
 */
export type FieldErrors = Record<string, string>

export function validateTemplate(input: ExerciseTemplateInput): FieldErrors {
  const errors: FieldErrors = {}

  if (!input.title.trim()) errors.title = 'blockTitleRequired'
  if (input.sets == null || input.sets < 1) errors.sets = 'setsMin'
  if (input.reps_per_set == null || input.reps_per_set < 1) errors.reps_per_set = 'repsMin'
  if (input.rest_seconds == null || input.rest_seconds < 0) errors.rest_seconds = 'restMin'
  if (input.set_rest_seconds == null || input.set_rest_seconds < 0) errors.set_rest_seconds = 'restMin'

  if (input.mode === 'timed') {
    if (input.work_seconds == null || input.work_seconds < 1) errors.work_seconds = 'workRequired'
  } else if (input.reps_target == null || input.reps_target < 1) {
    errors.reps_target = 'repsTargetRequired'
  }

  if (input.weight_percent != null && (input.weight_percent < 0 || input.weight_percent > 200)) {
    errors.weight_percent = 'weightRange'
  }
  if (input.rpe != null && (input.rpe < 1 || input.rpe > 10)) {
    errors.rpe = 'rpeRange'
  }
  if (input.alternate_side && input.side_change_seconds != null && input.side_change_seconds < 1) {
    errors.side_change_seconds = 'sideChangeMin'
  }
  if (input.transition_kind === 'timed' && (input.transition_seconds == null || input.transition_seconds < 1)) {
    errors.transition_seconds = 'transitionRequired'
  }

  return errors
}

export function validateForm(form: CustomExerciseFormData): FieldErrors {
  const errors: FieldErrors = {}

  if (!form.title.trim()) errors.title = 'titleRequired'
  if (!form.categoryId) errors.categoryId = 'categoryRequired'

  const duration = form.durationMinutes.trim()
  if (duration && !(Number.parseInt(duration, 10) > 0)) errors.durationMinutes = 'durationPositive'

  form.templates.forEach((template, index) => {
    const templateErrors = validateTemplate(template)
    for (const [field, message] of Object.entries(templateErrors)) {
      errors[`templates.${index}.${field}`] = message
    }
  })

  return errors
}

export function isValid(errors: FieldErrors): boolean {
  return Object.keys(errors).length === 0
}

/**
 * How long a block takes, following the same phase sequence the timer walks:
 * work (twice, plus the side change, when it alternates), rest between bouts,
 * set rest between sets. Manual blocks have no answer — the athlete decides how
 * long a bout lasts — so they return null.
 */
export function templateDurationSeconds(input: ExerciseTemplateInput): number | null {
  if (input.mode === 'manual') return null
  if (input.work_seconds == null || input.work_seconds < 1) return null

  const sets = input.sets
  const reps = input.reps_per_set
  if (sets == null || sets < 1 || reps == null || reps < 1) return null

  const sideChange = input.alternate_side ? (input.side_change_seconds ?? 0) : 0
  const bout = input.work_seconds * (input.alternate_side ? 2 : 1) + sideChange
  const set = bout * reps + (input.rest_seconds ?? 0) * (reps - 1)

  return set * sets + (input.set_rest_seconds ?? 0) * (sets - 1)
}

/**
 * Total for the whole exercise, or null as soon as one block is manual.
 *
 * Timed pauses between blocks count; a manual pause does not, because its
 * length is whatever the athlete takes — the same reason a manual block has no
 * duration at all. The last block's transition is ignored: nothing follows it.
 */
export function totalDurationSeconds(templates: ExerciseTemplateInput[]): number | null {
  let total = 0

  for (const [index, template] of templates.entries()) {
    const seconds = templateDurationSeconds(template)
    if (seconds == null) return null
    total += seconds

    const isLast = index === templates.length - 1
    if (!isLast && template.transition_kind === 'timed') {
      total += template.transition_seconds ?? 0
    }
  }

  return total
}

/** "1h 05m" / "4m 30s" / "45s" — a compact duration for the summary lines. */
export function formatSeconds(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const rest = seconds % 60

  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  if (minutes > 0) return rest > 0 ? `${minutes}m ${String(rest).padStart(2, '0')}s` : `${minutes}m`
  return `${rest}s`
}
